import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { PASSWORD_RESET_2 } from "./AuthActions";

import queryString from 'query-string';

function ResetPasswordConfirm() {
  const location = useLocation();
  const [formData, setFormData] = useState({
    new_password1: '',
    new_password2: '',
    uid: '',
    token: ''
  });
  const [error, setError] = useState('');

  useEffect(() => {
    try {
      // Parse the query parameters from the URL
      const parsed = queryString.parse(location.search);
      const resetLink = parsed["api_email_reset_confirm_link"];
      
      if (!resetLink) {
        setError('Invalid reset link. Please request a new password reset.');
        return;
      }

      // Extract uid and token from the reset link
      const stripped_parsed = resetLink.replace("/api/v1/auth/password-reset/confirm/", "");
      const split_stripped_parsed = stripped_parsed.split("/");

      if (split_stripped_parsed.length < 2) {
        setError('Invalid reset link format. Please request a new password reset.');
        return;
      }

      setFormData(prev => ({
        ...prev,
        uid: split_stripped_parsed[0],
        token: split_stripped_parsed[1]
      }));
    } catch (err) {
      setError('Error parsing reset link. Please request a new password reset.');
    }
  }, [location.search]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.id]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const { new_password1, new_password2, uid, token } = formData;
    
    if (!uid || !token) {
      setError('Invalid reset credentials. Please request a new password reset.');
      return;
    }
    
    if (new_password1 !== new_password2) {
      setError('Passwords do not match.');
      return;
    }
    
    PASSWORD_RESET_2(new_password1, new_password2, uid, token);
  };

  return (
    <>
      <style>{`
        /* Password manager resilient styles */
        .reset-password-input {
          background-color: var(--bg) !important;
          color: var(--ink) !important;
          border: 2px solid var(--border) !important;
        }
        .reset-password-input:-webkit-autofill,
        .reset-password-input:-webkit-autofill:hover,
        .reset-password-input:-webkit-autofill:focus,
        .reset-password-input:-webkit-autofill:active {
          -webkit-box-shadow: 0 0 0 30px var(--bg) inset !important;
          -webkit-text-fill-color: var(--ink) !important;
          background-color: var(--bg) !important;
          background-clip: padding-box !important;
          border: 2px solid var(--border) !important;
        }
        .reset-password-input:focus {
          border-color: var(--brand) !important;
          box-shadow: 0 0 0 3px rgba(89, 198, 193, 0.1) !important;
        }
        /* Force text visibility for password managers */
        .reset-password-input::selection {
          background: var(--brand) !important;
          color: var(--bg) !important;
        }
      `}</style>
      <div className="container" style={{ maxWidth: 500, padding: "48px 16px" }}>
      <header style={{ textAlign: "center", marginBottom: 48 }}>
        <h1 style={{ 
          fontSize: "32px", 
          fontWeight: "700",
          marginBottom: 16,
          color: "var(--ink)"
        }}>
          Choose New Password
        </h1>
        <p style={{ 
          color: "var(--muted)", 
          fontSize: "16px",
          lineHeight: "1.5"
        }}>
          Enter your new password below. Make sure it's secure and memorable.
        </p>
      </header>

      {error && (
        <div style={{
          padding: "12px 16px",
          backgroundColor: "#fee",
          border: "1px solid #fcc",
          borderRadius: "8px",
          marginBottom: 24,
          color: "#c33"
        }}>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} style={{ marginBottom: 32 }}>
          <div style={{ marginBottom: 24 }}>
            <label 
              htmlFor="new_password1"
              style={{
                display: "block",
                marginBottom: 8,
                fontWeight: "500",
                color: "var(--ink)",
                fontSize: "14px"
              }}
            >
              New Password
            </label>
            <input
              type="password"
              id="new_password1"
              className="reset-password-input"
              value={formData.new_password1}
              onChange={handleChange}
              required
              autoComplete="new-password"
              style={{
                width: "100%",
                padding: "12px 16px",
                borderRadius: "8px",
                fontSize: "16px",
                boxSizing: "border-box",
                transition: "border-color 0.2s ease, box-shadow 0.2s ease",
                outline: "none"
              }}
              placeholder="Enter your new password"
            />
          </div>

          <div style={{ marginBottom: 32 }}>
            <label 
              htmlFor="new_password2"
              style={{
                display: "block",
                marginBottom: 8,
                fontWeight: "500",
                color: "var(--ink)",
                fontSize: "14px"
              }}
            >
              Confirm New Password
            </label>
            <input
              type="password"
              id="new_password2"
              className="reset-password-input"
              value={formData.new_password2}
              onChange={handleChange}
              required
              autoComplete="new-password"
              style={{
                width: "100%",
                padding: "12px 16px",
                borderRadius: "8px",
                fontSize: "16px",
                boxSizing: "border-box",
                transition: "border-color 0.2s ease, box-shadow 0.2s ease",
                outline: "none"
              }}
              placeholder="Confirm your new password"
            />
          </div>

          <button
            type="submit"
            style={{
              width: "100%",
              padding: "14px 24px",
              backgroundColor: "var(--brand)",
              color: "white",
              border: "none",
              borderRadius: "8px",
              fontSize: "16px",
              fontWeight: "600",
              cursor: "pointer",
              transition: "background-color 0.2s ease"
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = "var(--brand-dark)";
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = "var(--brand)";
            }}
          >
            Update Password
          </button>
        </form>

        <div style={{ textAlign: "center" }}>
          <p style={{ color: "var(--muted)", fontSize: "14px" }}>
            Remember your password?{" "}
            <a 
              href="/accounts/signin"
              style={{
                color: "var(--brand)",
                textDecoration: "none",
                fontWeight: "500"
              }}
            >
              Sign in
            </a>
          </p>
        </div>
      </div>
    </>
  );
}

export default ResetPasswordConfirm;